import type { ChatMessage } from '../types';

const DB_NAME = 'volyx-chat-cache';
const DB_VERSION = 3;
const STORE_NAME = 'thread_messages';
const MAX_CACHED_MESSAGES = 100;

export type CacheEntry = {
  /** `event:<id>` or `direct:<id>` — see chatTarget.chatKey(). */
  chatKey: string;
  messages: ChatMessage[];
  hasMore: boolean;
  updatedAt: string;
};

// ----- DB open -----

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      // v1 keyed entries by eventId, which cannot address a direct thread. The cache is
      // disposable, so drop the old store rather than migrate its entries.
      if (db.objectStoreNames.contains('event_messages')) {
        db.deleteObjectStore('event_messages');
      }
      // v2 could persist hasMore: false next to a trimmed window, hiding "See older
      // messages" for good (see setThreadCache). Drop those entries so every thread refetches.
      if (db.objectStoreNames.contains(STORE_NAME)) {
        db.deleteObjectStore(STORE_NAME);
      }
      db.createObjectStore(STORE_NAME, { keyPath: 'chatKey' });
    };

    req.onsuccess = () => resolve(req.result);

    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };

    req.onblocked = () => {
      dbPromise = null;
      reject(new Error('[MessageCache] IndexedDB upgrade blocked'));
    };
  });

  return dbPromise;
}

// ----- Public API -----

export async function getThreadCache(
  key: string
): Promise<{ messages: ChatMessage[]; hasMore: boolean } | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);
      req.onsuccess = () => {
        const entry = req.result as CacheEntry | undefined;
        if (!entry) {
          resolve(null);
          return;
        }
        resolve({ messages: entry.messages, hasMore: entry.hasMore });
      };
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn('[MessageCache] getThreadCache failed', err);
    return null;
  }
}

export async function setThreadCache(
  key: string,
  data: { messages: ChatMessage[]; hasMore: boolean }
): Promise<void> {
  try {
    const db = await openDB();
    const trimmed = data.messages.slice(-MAX_CACHED_MESSAGES);
    const entry: CacheEntry = {
      chatKey: key,
      messages: trimmed,
      // Trimming drops the oldest messages, so the cached window always has more before it --
      // even if the in-memory list had loaded back to the start of the thread.
      hasMore: data.hasMore || trimmed.length < data.messages.length,
      updatedAt: new Date().toISOString(),
    };
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.put(entry);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn('[MessageCache] setThreadCache failed', err);
  }
}

export async function clearThreadCache(key: string): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.delete(key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    console.warn('[MessageCache] clearThreadCache failed', err);
  }
}

// ----- Merge helper (used by hook) -----

/**
 * Dedupe by id, sort by created_at. Does not trim — UI may hold more than the
 * IndexedDB window after “load older”; {@link setThreadCache} still keeps last MAX_CACHED_MESSAGES only.
 */
export function mergeMessages(existing: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  const byId = new Map<string, ChatMessage>(existing.map((m) => [m.id, m]));
  for (const m of incoming) byId.set(m.id, m);
  return Array.from(byId.values()).sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
}
